import { AlertCircle, CheckCircle2, Loader2, LockKeyhole, MailCheck, Paperclip, Plus, RefreshCw, Send, ShieldCheck, TicketCheck, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createMondayTask, fetchMondayTargets } from "./api";
import { readCurrentEmail, readHostContext } from "./office";
import { Field } from "./components/Field";
import type { Attachment, CurrentUser, AssignedUser, CreateTaskPayload, EmailMetadata, MondayBoardTarget, Priority, RequestType, TaskFormData } from "./types";

const requestTypes: RequestType[] = [
  "Website Update",
  "Website Bug",
  "Hardware Request",
  "NetSuite Issue",
  "NetSuite Change Request",
  "NetSuite Enhancement",
  "HubSpot Issue",
  "Access Request",
  "General IT",
  "Other"
];

const priorities: Priority[] = ["Low", "Medium", "High", "Urgent"];

const APP_MODE = new URLSearchParams(window.location.search).get("app") === "laguna" ? "laguna" : "outlooky";
const APP_NAME = APP_MODE === "laguna" ? "Laguna IT Tickets" : "Outlooky";
const PLATFORM = new URLSearchParams(window.location.search).get("platform") || "office";
const CURRENT_PATH = window.location.pathname.replace(/\/+$/, "") || "/";

function isBrowserOverviewRoute(): boolean {
  const hasAppParams = window.location.search.length > 0;
  const isRootPath = CURRENT_PATH === "/" || CURRENT_PATH.endsWith("/index.html");
  return isRootPath && !hasAppParams && typeof Office === "undefined";
}

function PublicPageHeader() {
  return (
    <header className="public-header">
      <a className="brand-mark" href="/" aria-label="Laguna IT Tickets overview">
        <TicketCheck size={22} aria-hidden="true" />
        <span>Laguna IT Tickets</span>
      </a>
      <nav aria-label="Legal pages">
        <a href="/privacy">Privacy</a>
        <a href="/terms">Terms</a>
      </nav>
    </header>
  );
}

function OverviewPage() {
  return (
    <main className="public-shell">
      <PublicPageHeader />

      <section className="overview-hero">
        <div className="hero-copy">
          <p className="eyebrow">Laguna Tools internal support</p>
          <h1>Submit IT requests from Outlook or Teams.</h1>
          <p>
            Laguna IT Tickets is an internal Microsoft 365 add-in for Laguna Tools employees and authorized internal users. It helps submit support requests to the IT team with the right context, routing, and monday.com tracking.
          </p>
        </div>
        <div className="hero-panel" aria-label="Ticket workflow summary">
          <div className="workflow-step">
            <MailCheck size={20} aria-hidden="true" />
            <span>Open from Outlook or Teams</span>
          </div>
          <div className="workflow-step">
            <TicketCheck size={20} aria-hidden="true" />
            <span>Describe the request</span>
          </div>
          <div className="workflow-step">
            <ShieldCheck size={20} aria-hidden="true" />
            <span>Submit to Laguna IT</span>
          </div>
        </div>
      </section>

      <section className="public-band" aria-labelledby="overview-title">
        <div>
          <h2 id="overview-title">Internal use only</h2>
          <p>
            This service is intended only for Laguna Tools internal users. Access may be limited by Microsoft 365, company domain validation, and administrative deployment settings.
          </p>
        </div>
        <div>
          <h2>Need access?</h2>
          <p>
            If you are a Laguna Tools user and do not see the add-in in Outlook or Teams, contact the IT team through your normal internal support channel.
          </p>
        </div>
      </section>

      <footer className="public-footer">
        <span>Laguna Tools internal IT support add-in</span>
        <span>
          <a href="/privacy">Privacy Policy</a> <a href="/terms">Terms of Use</a>
        </span>
      </footer>
    </main>
  );
}

function PrivacyPage() {
  return (
    <main className="public-shell legal-shell">
      <PublicPageHeader />

      <article className="legal-document">
        <p className="eyebrow">Effective May 18, 2026</p>
        <h1>Privacy Policy</h1>
        <p>
          Laguna IT Tickets is an internal support add-in for Laguna Tools employees and authorized internal users. It is used to collect and route IT support requests to Laguna Tools internal support systems.
        </p>

        <h2>Information We Collect</h2>
        <p>
          When you submit a ticket, the add-in may collect your name, email address, request title, request type, priority, supervisor email when provided, notes, and attachments you choose to upload. When opened from Outlook, it may also read limited metadata from the currently selected email, such as subject, sender, recipients, received time, message identifier, and an email body excerpt when supported by the add-in experience.
        </p>

        <h2>How We Use Information</h2>
        <p>
          Information is used only to create, triage, assign, investigate, and resolve internal IT support requests. Ticket data may be sent to Laguna Tools approved systems, including monday.com, through a secure backend service that keeps API credentials out of the add-in.
        </p>

        <h2>Access and Sharing</h2>
        <p>
          Ticket information is intended for Laguna Tools internal use. It may be visible to authorized IT personnel, supervisors, administrators, and other internal staff involved in resolving the request. We do not sell ticket information or make this add-in available for public consumer use.
        </p>

        <h2>Security</h2>
        <p>
          The add-in is designed for use inside Microsoft Outlook and Microsoft Teams and communicates with backend services over HTTPS. Access is intended for authorized Laguna Tools users only, and submitted requester email addresses may be validated against approved company domains.
        </p>

        <h2>Retention</h2>
        <p>
          Ticket records may be retained in internal support systems for operational history, auditing, reporting, and follow-up. Retention periods are governed by Laguna Tools internal policies and the configuration of connected systems.
        </p>

        <h2>Your Responsibilities</h2>
        <p>
          Do not submit personal, financial, medical, or highly sensitive information unless it is necessary for IT support. Avoid attaching confidential files unless the IT team needs them to resolve the request.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about this privacy policy or the handling of internal support ticket data should be directed to Laguna Tools IT through approved internal support channels.
        </p>
      </article>
    </main>
  );
}

function TermsPage() {
  return (
    <main className="public-shell legal-shell">
      <PublicPageHeader />

      <article className="legal-document">
        <p className="eyebrow">Effective May 18, 2026</p>
        <h1>Terms of Use</h1>
        <p>
          These terms apply to Laguna IT Tickets, an internal add-in provided for Laguna Tools employees and authorized internal users. By using the add-in, you agree to use it only for legitimate Laguna Tools business support needs.
        </p>

        <h2>Authorized Internal Use</h2>
        <p>
          The add-in is not a public service. Access is limited to Laguna Tools internal users who are permitted to submit IT support requests. You may not share access, attempt to bypass access controls, or use the service on behalf of unauthorized external parties.
        </p>

        <h2>Acceptable Use</h2>
        <p>
          Use the add-in to submit accurate, work-related IT support requests. Provide enough detail for IT to investigate the issue, and only attach files that are relevant to the request.
        </p>

        <h2>Prohibited Use</h2>
        <p>
          You may not use the add-in to submit false, abusive, unlawful, malicious, or unrelated content. You may not attempt to probe, disrupt, overload, reverse engineer, or misuse the add-in, backend service, monday.com integration, Microsoft 365 environment, or any connected system.
        </p>

        <h2>No Public Warranty</h2>
        <p>
          The add-in is provided as an internal business tool. Availability, features, routing, integrations, and access may change as Laguna Tools improves or maintains its internal support process.
        </p>

        <h2>Monitoring and Administration</h2>
        <p>
          Usage may be logged, reviewed, or administered by authorized Laguna Tools personnel for support, security, auditing, troubleshooting, and compliance purposes.
        </p>

        <h2>Changes</h2>
        <p>
          Laguna Tools may update these terms as the internal support workflow changes. Continued use of the add-in after updates means you accept the revised terms.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about these terms should be directed to Laguna Tools IT through approved internal support channels.
        </p>
      </article>
    </main>
  );
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function emailDomain(value: string): string {
  return isValidEmail(value) ? value.trim().toLowerCase().split("@").pop() || "" : "";
}

function validateSupervisorEmail(supervisorEmail: string, requesterEmail: string): string {
  const supervisor = supervisorEmail.trim();

  if (!supervisor) {
    return "";
  }

  if (!isValidEmail(supervisor)) {
    return "Requester supervisor email must be valid when provided.";
  }

  const requesterDomain = emailDomain(requesterEmail);
  const supervisorDomain = emailDomain(supervisor);

  if (requesterDomain && supervisorDomain !== requesterDomain) {
    return `Requester supervisor email must use the same company domain as the requester: ${requesterDomain}.`;
  }

  return "";
}

function initialForm(email: EmailMetadata | null, user: CurrentUser, defaultBoardKey: string): TaskFormData {
  return {
    boardKey: defaultBoardKey,
    title: "",
    requestType: "",
    priority: "",
    dueDate: "",
    assignedTo: "",
    requesterEmail: user.email || email?.fromEmail || "",
    supervisorEmail: "",
    notes: "",
    includeEmailBody: false,
    attachments: []
  };
}

function TicketApp() {
  const [email, setEmail] = useState<EmailMetadata | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [boards, setBoards] = useState<MondayBoardTarget[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
  const [form, setForm] = useState<TaskFormData | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<{ itemId: string } | null>(null);

  async function loadEmail() {
    console.log("App: loadEmail started. Platform:", PLATFORM, "App Mode:", APP_MODE);
    setLoadingEmail(true);
    setError("");
    setSuccess(null);

    try {
      console.log("App: Calling readHostContext...");
      const hostContext = await readHostContext(PLATFORM);
      console.log("App: readHostContext succeeded. Platform resolved to:", hostContext.platform);
      
      console.log("App: Fetching data (metadata and targets)...");
      const [metadata, targets] = await Promise.all([
        hostContext.platform === "outlook" ? readCurrentEmail() : Promise.resolve(null),
        fetchMondayTargets()
      ]);
      console.log("App: Data fetch succeeded. Boards found:", targets.boards.length);

      if (targets.boards.length === 0) {
        throw new Error(`No monday.com boards are configured for ${APP_NAME}.`);
      }

      setEmail(metadata);
      setUser(hostContext.user);
      setBoards(targets.boards);
      setAssignedUsers(targets.assignedUsers);
      setForm(initialForm(metadata, hostContext.user, targets.boards[0].key));
      console.log("App: Initialization complete.");
    } catch (loadError) {
      console.error("App: Initialization error:", loadError);
      setError(loadError instanceof Error ? loadError.message : "Unable to initialize the app.");
    } finally {
      setLoadingEmail(false);
    }
  }

  useEffect(() => {
    loadEmail();
  }, []);

  function updateForm<K extends keyof TaskFormData>(key: K, value: TaskFormData[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  }

  function createNewTaskForSelectedEmail() {
    if (boards.length === 0 || !user) {
      return;
    }

    setForm(initialForm(email, user, boards[0].key));
    setSuccess(null);
    setError("");
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    const newAttachments: Attachment[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > MAX_FILE_SIZE) {
        setError(`File "${file.name}" is too large. Maximum file size is 25MB.`);
        continue;
      }

      const reader = new FileReader();

      const content = await new Promise<string>((resolve) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]); // Get only base64 part
        };
        reader.readAsDataURL(file);
      });

      newAttachments.push({
        name: file.name,
        content
      });
    }

    setForm((current) => (current ? { ...current, attachments: [...current.attachments, ...newAttachments] } : current));
    event.target.value = ""; // Reset input
  }

  function removeAttachment(index: number) {
    setForm((current) => {
      if (!current) return current;
      const attachments = [...current.attachments];
      attachments.splice(index, 1);
      return { ...current, attachments };
    });
  }

  function payloadFromForm(currentForm: TaskFormData, metadata: EmailMetadata | null): CreateTaskPayload {
    return {
      boardKey: currentForm.boardKey,
      title: currentForm.title,
      requestType: currentForm.requestType,
      priority: currentForm.priority,
      dueDate: currentForm.dueDate,
      assignedTo: currentForm.assignedTo,
      requesterEmail: currentForm.requesterEmail,
      supervisorEmail: currentForm.supervisorEmail,
      notes: currentForm.notes,
      attachments: currentForm.attachments,
      emailMetadata: {
        subject: metadata?.subject || "",
        fromName: metadata?.fromName || "",
        fromEmail: metadata?.fromEmail || "",
        recipients: metadata?.recipients || [],
        receivedDateTime: metadata?.receivedDateTime || "",
        messageId: metadata?.messageId || ""
      }
    };
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!form || !user) {
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess(null);

    try {
      const supervisorValidationError = validateSupervisorEmail(form.supervisorEmail, form.requesterEmail);

      if (supervisorValidationError) {
        throw new Error(supervisorValidationError);
      }

      const response = await createMondayTask(payloadFromForm(form, email));
      setSuccess({ itemId: response.itemId || "" });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : `Unable to create the monday.com ticket.`);
    } finally {
      setSubmitting(false);
    }
  }

  if (success && user) {
    return (
      <main className="app-shell">
        <header className="pane-header">
          <div>
            <p className="eyebrow">{APP_NAME}</p>
            <h1>Ticket Created</h1>
          </div>
          <button className="icon-button" type="button" onClick={loadEmail} aria-label="Refresh email details">
            <RefreshCw size={18} aria-hidden="true" />
          </button>
        </header>

        <section className="result-panel success-panel" role="status" aria-live="polite">
          <CheckCircle2 size={34} aria-hidden="true" />
          <div>
            <h2>monday.com ticket created successfully</h2>
            <p>Item ID: {success.itemId}</p>
          </div>
        </section>

        <div className="action-row">
          <button className="primary-button" type="button" onClick={createNewTaskForSelectedEmail}>
            <Plus size={17} aria-hidden="true" />
            Create New Ticket
          </button>
          <button className="secondary-button" type="button" onClick={loadEmail}>
            <RefreshCw size={16} aria-hidden="true" />
            Reload
          </button>
        </div>
      </main>
    );
  }

  if (loadingEmail) {
    return (
      <main className="app-shell centered">
        <Loader2 className="spin" size={28} aria-hidden="true" />
        <p>{PLATFORM === "teams" ? "Initializing application..." : "Reading email details..."}</p>
      </main>
    );
  }

  if (!form || !user) {
    return (
      <main className="app-shell">
        <header className="pane-header">
          <div>
            <p className="eyebrow">{APP_NAME}</p>
            <h1>Initialization Error</h1>
          </div>
        </header>
        <section className="banner error" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error || "Application initialization failed."}</span>
        </section>
        <button className="secondary-button" type="button" onClick={loadEmail}>
          <RefreshCw size={16} aria-hidden="true" />
          Retry
        </button>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="pane-header">
        <div>
          <p className="eyebrow">{APP_NAME}</p>
          <h1>{APP_MODE === "laguna" ? "Submit IT Ticket" : "Create monday.com Task"}</h1>
        </div>
        <button className="icon-button" type="button" onClick={loadEmail} aria-label="Refresh email details">
          <RefreshCw size={18} aria-hidden="true" />
        </button>
      </header>

      {error ? (
        <section className="banner error" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
        </section>
      ) : null}

      <form className="task-form" onSubmit={handleSubmit}>
        {APP_MODE === "outlooky" && (
          <Field label="monday.com Board" htmlFor="boardKey" required>
            <select
              id="boardKey"
              value={form.boardKey}
              required
              onChange={(event) => updateForm("boardKey", event.target.value)}
            >
              {boards.map((board) => (
                <option key={board.key} value={board.key}>
                  {board.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label={APP_MODE === "laguna" ? "Title" : "Task Title"} htmlFor="title" required>
          <input
            id="title"
            value={form.title}
            required
            onChange={(event) => updateForm("title", event.target.value)}
          />
        </Field>

        <div className="form-grid">
          <Field label="Request Type" htmlFor="requestType" required>
            <select
              id="requestType"
              value={form.requestType}
              required
              onChange={(event) => updateForm("requestType", event.target.value as RequestType)}
            >
              <option value="" disabled>
                Select request type
              </option>
              {requestTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Priority" htmlFor="priority" required>
            <select
              id="priority"
              value={form.priority}
              required
              onChange={(event) => updateForm("priority", event.target.value as Priority)}
            >
              <option value="" disabled>
                Select priority
              </option>
              {priorities.map((priority) => (
                <option key={priority} value={priority}>
                  {priority}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {APP_MODE === "outlooky" && (
          <div className="form-grid">
            <Field label="Due Date" htmlFor="dueDate">
              <input id="dueDate" type="date" value={form.dueDate} onChange={(event) => updateForm("dueDate", event.target.value)} />
            </Field>

            <Field label="Assigned To" htmlFor="assignedTo">
              <select
                id="assignedTo"
                value={form.assignedTo}
                onChange={(event) => updateForm("assignedTo", event.target.value)}
              >
                <option value="">Unassigned</option>
                {assignedUsers.map((user) => (
                  <option key={user.id} value={user.id.toString()}>
                    {user.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        <Field label="Requester Email" htmlFor="requesterEmail" required>
          <input
            id="requesterEmail"
            type="email"
            value={form.requesterEmail}
            required
            onChange={(event) => updateForm("requesterEmail", event.target.value)}
          />
        </Field>

        <Field label="Requester Supervisor Email" htmlFor="supervisorEmail">
          <input
            id="supervisorEmail"
            type="email"
            value={form.supervisorEmail}
            onChange={(event) => updateForm("supervisorEmail", event.target.value)}
          />
        </Field>

        <Field label={APP_MODE === "laguna" ? "Description" : "Notes"} htmlFor="notes">
          <textarea
            id="notes"
            value={form.notes}
            rows={APP_MODE === "laguna" ? 5 : 4}
            onChange={(event) => updateForm("notes", event.target.value)}
          />
        </Field>

        <div className="field">
          <span className="field-label">Attachments</span>
          <div className="attachments-container">
            <div className="file-input-wrapper">
              <label htmlFor="attachments" className="file-input-label">
                <Paperclip size={16} />
                Add Files
              </label>
              <input
                id="attachments"
                type="file"
                multiple
                className="hidden-input"
                onChange={handleFileChange}
              />
            </div>

            {form.attachments.length > 0 && (
              <ul className="attachment-list">
                {form.attachments.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="attachment-item">
                    <span className="file-name" title={file.name}>{file.name}</span>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => removeAttachment(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <button className="submit-button" type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="spin" size={18} aria-hidden="true" />
              {APP_MODE === "laguna" ? "Submitting Ticket..." : "Creating Task..."}
            </>
          ) : (
            <>
              <Send size={18} aria-hidden="true" />
              {APP_MODE === "laguna" ? "Submit IT Ticket" : "Create Task"}
            </>
          )}
        </button>
      </form>
    </main>
  );
}

export default function App() {
  if (CURRENT_PATH === "/privacy") {
    return <PrivacyPage />;
  }

  if (CURRENT_PATH === "/terms") {
    return <TermsPage />;
  }

  if (isBrowserOverviewRoute()) {
    return <OverviewPage />;
  }

  return <TicketApp />;
}
